/*
 * Copyright (c) 2021. Dave van Rijn Development
 */

import './style/simpleButton.scss';

import React, {CSSProperties, PureComponent, ReactNode} from 'react';
import {ControlContext} from "../util/controlContext";
import {colorIsWhite, convertColor, editColor} from "../util/colorUtil";
import {ControlPadding, ElementPosition, OrnamentShape} from "../util/interfaces";
import classNames from "classnames";
import {voidFunction} from "../util/controlUtil";

interface Props {
    onClick: any;
    onMouseEnter: any;
    onMouseLeave: any;
    onMouseDown: any;
    label: string;
    containerClass: string;
    labelClass: string;
    id: string;
    padding: ControlPadding;
    disabled: boolean;
    isHovered: boolean;
    baseColor?: string;
    textColor?: string;
    title?: string;
    withRipple: boolean;
    primary: boolean;
    tabIndex?: number;
    children?: ReactNode | ReactNode[] | OrnamentShape | OrnamentShape[];
}

export default class SimpleButton extends PureComponent<Props> {
    declare context: React.ContextType<typeof ControlContext>;
    static contextType = ControlContext;

    getBaseColor = (trueBase: boolean = false): string => {
        const {baseColor, textColor, primary} = this.props;
        let color: string;
        if (baseColor && baseColor.length) return convertColor(baseColor);
        else color = this.context.baseColor;
        color = convertColor(color);
        if (colorIsWhite(color) && !trueBase && primary) {
            if (textColor && textColor.length) color = textColor;
            else color = this.context.contrastColor;
            color = convertColor(color);
        }
        return color;
    };

    getContrastColor = (allowWhite: boolean = true) => {
        const {textColor, primary, isHovered} = this.props, {baseColor, contrastColor} = this.context;
        if (textColor && textColor.length) return convertColor(textColor);
        const trueBase = this.getBaseColor(true);
        if (colorIsWhite(trueBase) && primary) return trueBase;

        let color: string;
        if (textColor && textColor.length) color = textColor;
        else {
            color = convertColor(contrastColor);
            if (!primary && !isHovered) {
                color = convertColor(baseColor);
                if (colorIsWhite(color) && !allowWhite)
                    color = convertColor(contrastColor);
            }
        }
        return color;
    };

    getContainerClass = (): string => {
        const {containerClass, padding, disabled, withRipple, primary} = this.props;
        return classNames('dvr-simple-button', padding, disabled && 'disabled', withRipple && 'ripple',
            primary && 'primary', containerClass);
    };

    getContainerStyle = (): CSSProperties => {
        const {disabled, isHovered, primary} = this.props, baseColor = this.getBaseColor();
        let backgroundColor: string;
        if (disabled) backgroundColor = 'color-gray-6';
        else if (isHovered) {
            if (primary) backgroundColor = editColor(-.2, baseColor);
            else backgroundColor = editColor(-.2, baseColor);
        } else if (!primary) backgroundColor = 'transparent';
        else backgroundColor = baseColor;
        return {backgroundColor: convertColor(backgroundColor)};
    };

    getLabelStyle = (): CSSProperties => {
        const {disabled} = this.props, textColor = this.getContrastColor(false),
            style: CSSProperties = {};
        let color: string;
        if (disabled) color = 'gray';
        else color = textColor;
        style.color = convertColor(color);

        const childCount = this.getLabelChildCount();
        style.gridTemplateColumns = `repeat(${childCount}, auto)`;
        return style;
    };

    getLabelChildCount = () => {
        const {label, children} = this.props;
        let childCount = 0;
        if (label && label.length) childCount++;
        if (Array.isArray(children)) {
            for (const child of children) {
                if (this.isOrnament(child)) childCount++;
            }
        } else if (this.isOrnament(children)) childCount++;
        return Math.min(3, childCount);
    };

    isOrnament = (obj: any) => obj && obj.hasOwnProperty('element');

    placementIsMatch = (ornament: OrnamentShape, placement: ElementPosition) => ornament.placement === placement;

    renderOrnament = (placement: ElementPosition) => {
        const {children, onClick} = this.props;
        let ornament: OrnamentShape | null = null;
        if (Array.isArray(children)) {
            for (const child of children) {
                if (this.isOrnament(child) && this.placementIsMatch(child as OrnamentShape, placement)) {
                    ornament = child as OrnamentShape;
                    break;
                }
            }
        } else if (this.isOrnament(children) && this.placementIsMatch(children as OrnamentShape, placement)) {
            ornament = children as OrnamentShape;
        }
        if (ornament !== null) return (
            <div className={classNames('dvr-simple-button-ornament', placement)}
                 onClick={ornament.allowPropagation ? onClick : voidFunction}>
                {ornament.element}
            </div>
        );
        return null;
    };

    renderLabel = () => {
        const {label, labelClass} = this.props;
        return <label className={classNames('dvr-simple-button-label', labelClass)}>{label}</label>;
    };

    render = () => {
        const {onClick, onMouseEnter, onMouseLeave, id, onMouseDown, withRipple, title, disabled, tabIndex} = this.props,
            titleProp = title !== undefined ? {title} : {};
        return (
            <div className={this.getContainerClass()} style={this.getContainerStyle()}
                 onClick={disabled ? voidFunction : onClick}
                 onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave} onMouseDown={onMouseDown}
                 id={id} tabIndex={tabIndex} {...titleProp}>
                <div className='dvr-simple-button-label-container' style={this.getLabelStyle()}>
                    {this.renderOrnament(ElementPosition.LEFT)}
                    {this.renderLabel()}
                    {this.renderOrnament(ElementPosition.RIGHT)}
                </div>
                {withRipple && <div className='ripple' style={{backgroundColor: this.getContrastColor()}}/>}
            </div>
        )
    };
}